import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import RemoveIcon from "@mui/icons-material/Remove";
import UndoIcon from "@mui/icons-material/Undo";
import type {
  WorkflowApprover,
  WorkflowDecision,
  WorkflowHistoryEvent,
  WorkflowHistoryResponse,
  WorkflowState,
  WorkflowTask,
} from "../../types/workflow";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import {
  getDocumentWorkflowHistory,
  proceedDocumentWorkflow,
} from "../../api/workflow";
import { useEffect, useMemo, useState } from "react";

type Props = {
  workflow: WorkflowState | null;
  loading: boolean;
  error: string | null;
  companyId: string;
  documentId: string;
  onWorkflowChanged: () => Promise<void> | void;
};

function approverLabel(approver: WorkflowApprover): string {
  const fullName = [approver.firstName, approver.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || approver.email;
}

function approverInitials(approver: WorkflowApprover): string {
  const initials = [approver.firstName, approver.lastName]
    .filter(Boolean)
    .map((value) => String(value).trim().charAt(0).toUpperCase())
    .join("");

  if (initials) return initials.slice(0, 2);

  return approver.email.slice(0, 2).toUpperCase();
}

function nodePresentation(task: WorkflowTask, isCurrent: boolean) {
  if (task.status === "Approved") {
    return {
      borderColor: "success.main",
      backgroundColor: "success.main",
      color: "success.contrastText",
      icon: <CheckIcon sx={{ fontSize: 15 }} />,
      dashed: false,
    };
  }

  if (task.status === "Skipped") {
    return {
      borderColor: "grey.400",
      backgroundColor: "grey.100",
      color: "grey.600",
      icon: <RemoveIcon sx={{ fontSize: 16 }} />,
      dashed: false,
    };
  }

  if (task.status === "CanceledByReturn") {
    return {
      borderColor: "warning.main",
      backgroundColor: "background.paper",
      color: "warning.main",
      icon: <UndoIcon sx={{ fontSize: 14 }} />,
      dashed: false,
    };
  }

  if (task.status === "Canceled") {
    return {
      borderColor: "error.main",
      backgroundColor: "background.paper",
      color: "error.main",
      icon: <CloseIcon sx={{ fontSize: 14 }} />,
      dashed: false,
    };
  }

  return {
    borderColor: isCurrent ? "primary.main" : "grey.400",
    backgroundColor: "background.paper",
    color: isCurrent ? "primary.main" : "text.secondary",
    icon: null,
    dashed: false,
  };
}

function isZeroDate(value: string | null | undefined): boolean {
  return !value || value.startsWith("0001-01-01");
}

function formatDateTime(value: string | null | undefined): string {
  if (isZeroDate(value)) return "";

  const parsed = new Date(value as string);
  if (Number.isNaN(parsed.getTime())) return value ?? "";

  return parsed.toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function dueLabel(value: string | null | undefined): string | null {
  if (isZeroDate(value)) return null;

  const due = new Date(value as string);
  if (Number.isNaN(due.getTime())) return null;

  const diff = due.getTime() - Date.now();
  const days = Math.ceil(diff / 86_400_000);

  if (days < 0) return `${Math.abs(days)} dni po terminie`;
  if (days === 0) return "dzisiaj";
  if (days === 1) return "1 dzień";
  return `${days} dni`;
}

function historyActor(event: WorkflowHistoryEvent): string {
  const user = event.userId?.trim() || event.completedBy?.trim() || "BC";
  const onBehalfOf = event.onBehalfOf?.trim();

  return onBehalfOf ? `${user} (w imieniu ${onBehalfOf})` : user;
}

function historyNodeLabel(
  rawNodeNo: string | number | null | undefined,
  tasks: WorkflowTask[]
): string | null {
  const nodeNo = Number(rawNodeNo);
  if (!Number.isFinite(nodeNo) || nodeNo <= 0) return null;

  const index = tasks.findIndex((task) => Number(task.nodeNo) === nodeNo);
  if (index < 0) return `Węzeł ${nodeNo}`;

  const name = tasks[index].name?.trim();
  return `Węzeł ${index + 1}${name ? ` · ${name}` : ""}`;
}

function historyEventNode(
  event: WorkflowHistoryEvent,
  tasks: WorkflowTask[]
): string | null {
  const normalizedEvent = event.event.trim().toLowerCase();

  const candidates =
    normalizedEvent === "returned"
      ? [event.newValue, event.nodeNo, event.taskLineNo, event.oldValue]
      : normalizedEvent === "rejected"
      ? [event.taskLineNo, event.nodeNo, event.oldValue, event.newValue]
      : [event.newValue, event.nodeNo, event.taskLineNo, event.oldValue];

  for (const candidate of candidates) {
    const label = historyNodeLabel(candidate, tasks);
    if (label) return label;
  }

  return null;
}

function historyEventText(
  event: WorkflowHistoryEvent,
  tasks: WorkflowTask[]
): string {
  const actor = historyActor(event);
  const node = historyEventNode(event, tasks);
  const normalizedEvent = event.event.trim().toLowerCase();

  if (normalizedEvent === "rejected") {
    return `${actor} odrzucił(a) workflow${node ? ` · ${node}` : ""}`;
  }

  if (normalizedEvent === "returned") {
    return node
      ? `${actor} cofnął/cofnęła proces do ${node}`
      : `${actor} cofnął/cofnęła workflow`;
  }

  if (normalizedEvent === "process activated") {
    return node
      ? `${actor} · przejście do ${node}`
      : `${actor} · przejście procesu`;
  }

  if (normalizedEvent === "approved") {
    return node
      ? `${actor} zatwierdził(a) ${node}`
      : `${actor} zatwierdził(a) krok`;
  }

  if (normalizedEvent === "skipped") {
    return node ? `Pominięto ${node}` : "Pominięto krok workflow";
  }

  if (normalizedEvent === "canceled") {
    return node ? `Anulowano ${node}` : "Anulowano krok workflow";
  }

  return `${actor} · ${event.event || "Zdarzenie workflow"}${
    node ? ` · ${node}` : ""
  }`;
}

function historyEventPresentation(event: WorkflowHistoryEvent) {
  const normalizedEvent = event.event.trim().toLowerCase();

  if (normalizedEvent === "rejected") {
    return { color: "error.main", icon: <CloseIcon sx={{ fontSize: 14 }} /> };
  }

  if (normalizedEvent === "returned") {
    return { color: "warning.main", icon: <UndoIcon sx={{ fontSize: 14 }} /> };
  }

  if (normalizedEvent === "process activated") {
    return {
      color: "primary.main",
      icon: <KeyboardArrowRightIcon sx={{ fontSize: 14 }} />,
    };
  }

  if (normalizedEvent === "skipped" || normalizedEvent === "canceled") {
    return { color: "text.secondary", icon: <RemoveIcon sx={{ fontSize: 14 }} /> };
  }

  return { color: "success.main", icon: <CheckIcon sx={{ fontSize: 14 }} /> };
}

function shouldShowHistoryComment(event: WorkflowHistoryEvent): boolean {
  const comment = event.comment?.trim();
  if (!comment) return false;

  return !(
    event.event.trim().toLowerCase() === "process activated" &&
    comment.toLowerCase() === "advanced to next workflow task."
  );
}

function parseHistoryDate(value: string | null | undefined): Date | null {
  if (!value) return null;

  const normalized = value.replace(
    /(\.\d{3})\d+(Z|[+-]\d{2}:\d{2})$/,
    "$1$2"
  );
  const parsed = new Date(normalized);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function historyActorDisplayName(event: WorkflowHistoryEvent): string {
  const rawActor = event.userId?.trim() || event.completedBy?.trim();
  if (!rawActor) return "Business Central";

  const localPart = rawActor.includes("@") ? rawActor.split("@")[0] : rawActor;
  const displayName = localPart
    .replace(/[._-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map(
      (part) =>
        `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`
    )
    .join(" ");

  const onBehalfOf = event.onBehalfOf?.trim();
  return onBehalfOf
    ? `${displayName || rawActor} · w imieniu ${onBehalfOf}`
    : displayName || rawActor;
}

function historyActorInitials(event: WorkflowHistoryEvent): string {
  const name = historyActorDisplayName(event).split("·")[0].trim();
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return initials || "BC";
}

function historyEventTitle(event: WorkflowHistoryEvent): string {
  switch (event.event.trim().toLowerCase()) {
    case "rejected":
      return "Odrzucono workflow";
    case "returned":
      return "Cofnięto do etapu";
    case "process activated":
      return "Przekazano do etapu";
    case "approved":
      return "Zatwierdzono etap";
    case "skipped":
      return "Pominięto etap";
    case "canceled":
      return "Anulowano etap";
    default:
      return event.event || "Zdarzenie workflow";
  }
}

function formatHistoryTime(value: string | null | undefined): string {
  const parsed = parseHistoryDate(value);
  if (!parsed) return "";

  return parsed.toLocaleTimeString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatHistoryDate(value: Date): string {
  return value.toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function startOfLocalDay(value: Date): number {
  return new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate()
  ).getTime();
}

function historyDayHeading(value: string | null | undefined): string {
  const parsed = parseHistoryDate(value);
  if (!parsed) return "BEZ DATY";

  const today = new Date();
  const dayDiff = Math.round(
    (startOfLocalDay(today) - startOfLocalDay(parsed)) / 86_400_000
  );

  const prefix =
    dayDiff === 0
      ? "DZISIAJ"
      : dayDiff === 1
      ? "WCZORAJ"
      : parsed
          .toLocaleDateString("pl-PL", { weekday: "long" })
          .toLocaleUpperCase("pl-PL");

  return `${prefix} · ${formatHistoryDate(parsed)}`;
}

function historyDayKey(value: string | null | undefined): string {
  const parsed = parseHistoryDate(value);
  if (!parsed) return "__NO_DATE__";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatRelativeHistoryDateTime(
  value: string | null | undefined
): string {
  const parsed = parseHistoryDate(value);
  if (!parsed) return "";

  const today = new Date();
  const dayDiff = Math.round(
    (startOfLocalDay(today) - startOfLocalDay(parsed)) / 86_400_000
  );
  const time = formatHistoryTime(value);

  if (dayDiff === 0) return `dzisiaj, ${time}`;
  if (dayDiff === 1) return `wczoraj, ${time}`;
  return `${formatHistoryDate(parsed)}, ${time}`;
}

function returnCountLabel(count: number): string {
  if (count === 1) return "1 cofnięcie";
  if (count > 1 && count < 5) return `${count} cofnięcia`;
  return `${count} cofnięć`;
}

function terminalPresentation(status: string | null | undefined) {
  if (status === "Rejected") {
    return {
      label: "Odrzucona",
      color: "error.main",
      icon: <CloseIcon sx={{ fontSize: 19 }} />,
    };
  }

  if (status === "Canceled") {
    return {
      label: "Anulowana",
      color: "text.secondary",
      icon: <RemoveIcon sx={{ fontSize: 19 }} />,
    };
  }

  return {
    label: "Zatwierdzona",
    color: "success.main",
    icon: <CheckIcon sx={{ fontSize: 17 }} />,
  };
}

export default function DocumentWorkflow({
  workflow,
  loading,
  error,
  companyId,
  documentId,
  onWorkflowChanged,
}: Props) {
  const { t } = useAppTranslation("common");
  const [decisionLoading, setDecisionLoading] =
    useState<WorkflowDecision | null>(null);
  const [dialogDecision, setDialogDecision] =
    useState<"Returned" | "Rejected" | null>(null);
  const [comment, setComment] = useState("");
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [history, setHistory] = useState<WorkflowHistoryResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;

    if (!workflow?.hasInstance) {
      setHistory(null);
      setHistoryError(null);
      setHistoryLoading(false);
      return () => {
        active = false;
      };
    }

    const loadHistory = async () => {
      setHistoryLoading(true);
      setHistoryError(null);

      try {
        const result = await getDocumentWorkflowHistory(companyId, documentId);
        if (active) setHistory(result);
      } catch (historyLoadError) {
        console.error(historyLoadError);
        if (active) {
          setHistoryError(
            historyLoadError instanceof Error
              ? historyLoadError.message
              : "Nie udało się pobrać historii workflow."
          );
        }
      } finally {
        if (active) setHistoryLoading(false);
      }
    };

    void loadHistory();

    return () => {
      active = false;
    };
  }, [
    companyId,
    documentId,
    workflow?.hasInstance,
    workflow?.instanceEntryNo,
    historyRefreshKey,
  ]);

  const effectiveInstanceStatus =
    workflow?.instanceStatus ?? history?.instance?.status ?? null;

  const currentTask = useMemo(() => {
    if (!workflow) return undefined;

    return (
      workflow.tasks.find(
        (task) =>
          task.status === "Open" && task.nodeNo === workflow.currentNodeNo
      ) ?? workflow.tasks.find((task) => task.status === "Open")
    );
  }, [workflow]);

  const executeDecision = async (
    decision: WorkflowDecision,
    decisionComment = ""
  ) => {
    setDecisionLoading(decision);
    setDecisionError(null);

    try {
      await proceedDocumentWorkflow(
        companyId,
        documentId,
        decision,
        decisionComment
      );

      setDialogDecision(null);
      setComment("");
      setHistoryRefreshKey((current) => current + 1);
      await onWorkflowChanged();
    } catch (actionError) {
      console.error(actionError);
      setDecisionError(
        actionError instanceof Error
          ? actionError.message
          : t("Nie udało się wykonać decyzji workflow.")
      );
    } finally {
      setDecisionLoading(null);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ mb: 2, px: 2.25, py: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">
            {t("Ładowanie workflow akceptacji...")}
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!workflow?.hasInstance) {
    return null;
  }

  const currentApprovals =
    currentTask?.currentApprovals ??
    workflow.approvers.filter((approver) => approver.approved).length;
  const requiredApprovals = currentTask?.requiredApprovals ?? 0;
  const canTakeAction =
    workflow.currentUser.isApprover &&
    !workflow.currentUser.approved &&
    !workflow.closed &&
    effectiveInstanceStatus !== "Rejected" &&
    effectiveInstanceStatus !== "Canceled";
  const actionBusy = decisionLoading !== null;
  const commentRequired = dialogDecision !== null;
  const trimmedComment = comment.trim();
  const currentIndex = currentTask
    ? workflow.tasks.findIndex((task) => task === currentTask)
    : -1;
  const currentDueLabel = dueLabel(history?.currentTaskDueDateTime);

  const historyEvents = [...(history?.events ?? [])].sort((left, right) => {
    const leftTime = parseHistoryDate(left.occurredAt)?.getTime() ?? 0;
    const rightTime = parseHistoryDate(right.occurredAt)?.getTime() ?? 0;

    if (leftTime !== rightTime) return rightTime - leftTime;
    return (right.entryNo ?? 0) - (left.entryNo ?? 0);
  });
  const lastHistoryEvent =
    historyEvents.length > 0 ? historyEvents[0] : null;
  const historyCount = history?.summary.total ?? historyEvents.length;

  const isTerminal =
    workflow.closed ||
    effectiveInstanceStatus === "Approved" ||
    effectiveInstanceStatus === "Rejected" ||
    effectiveInstanceStatus === "Canceled";

  const historyGroups = historyEvents.reduce<
    Array<{
      key: string;
      label: string;
      events: WorkflowHistoryEvent[];
    }>
  >((groups, event) => {
    const key = historyDayKey(event.occurredAt);
    const lastGroup = groups[groups.length - 1];

    if (lastGroup?.key === key) {
      lastGroup.events.push(event);
      return groups;
    }

    groups.push({
      key,
      label: historyDayHeading(event.occurredAt),
      events: [event],
    });
    return groups;
  }, []);

  const renderHistoryTimeline = () => {
    if (historyLoading) {
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t("Ładowanie historii...")}
          </Typography>
        </Stack>
      );
    }

    if (historyError) {
      return <Alert severity="error">{historyError}</Alert>;
    }

    if (historyEvents.length === 0) {
      return (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 2, fontSize: "0.8125rem" }}
        >
          {t("Brak wpisów w historii workflow.")}
        </Typography>
      );
    }

    return (
      <Stack spacing={2.1}>
        {historyGroups.map((group) => (
          <Box key={group.key}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1.1,
                ml: 4.5,
                fontSize: "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: "text.secondary",
              }}
            >
              {group.label}
            </Typography>

            <Stack spacing={0}>
              {group.events.map((event, index) => {
                const presentation = historyEventPresentation(event);
                const nodeLabel = historyEventNode(event, workflow.tasks);
                const isLast = index === group.events.length - 1;

                return (
                  <Box
                    key={`${event.id}-${event.entryNo ?? index}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "34px minmax(0, 1fr)",
                        sm: "34px minmax(0, 1fr) 56px",
                      },
                      columnGap: 1.15,
                      position: "relative",
                      pb: isLast ? 0 : 2.15,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                      }}
                    >
                      {!isLast && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 31,
                            bottom: -17,
                            width: "1.5px",
                            bgcolor: "divider",
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          border: "1px solid",
                          borderColor: presentation.color,
                          color: presentation.color,
                          bgcolor: "background.paper",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1,
                        }}
                      >
                        {presentation.icon}
                      </Box>
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Stack
                        direction="row"
                        spacing={0.8}
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ minHeight: 30 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.8125rem",
                            lineHeight: 1.35,
                            fontWeight: 600,
                            color: presentation.color,
                          }}
                        >
                          {historyEventTitle(event)}
                        </Typography>

                        {nodeLabel && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={nodeLabel}
                            sx={{
                              height: 22,
                              borderRadius: 1,
                              bgcolor: "action.hover",
                              borderColor: "divider",
                              color: "text.secondary",
                              "& .MuiChip-label": {
                                px: 0.9,
                                fontSize: "0.6875rem",
                                fontWeight: 500,
                              },
                            }}
                          />
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        sx={{ mt: 0.25 }}
                      >
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            fontSize: "0.5625rem",
                            fontWeight: 700,
                            bgcolor: "action.selected",
                            color: "primary.main",
                          }}
                        >
                          {historyActorInitials(event)}
                        </Avatar>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.75rem",
                            color: "text.primary",
                            lineHeight: 1.35,
                          }}
                        >
                          {historyActorDisplayName(event)}
                        </Typography>
                      </Stack>

                      {shouldShowHistoryComment(event) && (
                        <Box
                          sx={{
                            mt: 0.9,
                            px: 1.15,
                            py: 0.8,
                            maxWidth: 665,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                            display: "flex",
                            gap: 1,
                            alignItems: "baseline",
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              flex: "0 0 auto",
                              fontSize: "0.625rem",
                              fontWeight: 700,
                              letterSpacing: "0.05em",
                              color: "text.secondary",
                            }}
                          >
                            KOMENTARZ
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              minWidth: 0,
                              fontSize: "0.75rem",
                              lineHeight: 1.4,
                              color: "text.primary",
                            }}
                          >
                            {event.comment}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: { xs: "none", sm: "block" },
                        pt: 0.65,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        fontSize: "0.75rem",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatHistoryTime(event.occurredAt)}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: { xs: "block", sm: "none" },
                        gridColumn: "2",
                        mt: 0.45,
                        fontSize: "0.6875rem",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatHistoryTime(event.occurredAt)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  const renderHistory = () => (
    <>
      <Divider sx={{ mt: 1.25 }} />
      <Box>
        <Button
          size="small"
          color="inherit"
          onClick={() => setHistoryOpen((current) => !current)}
          startIcon={
            historyOpen ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )
          }
          sx={{
            px: 0.5,
            py: 0.5,
            width: "100%",
            minHeight: 34,
            textTransform: "none",
            justifyContent: "flex-start",
            borderRadius: 1,
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            letterSpacing: 0,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          {historyLoading ? (
            <Typography
              component="span"
              sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.secondary" }}
            >
              {t("Ładowanie historii...")}
            </Typography>
          ) : (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                columnGap: 0.5,
                rowGap: 0.1,
                textAlign: "left",
              }}
            >
              <Typography
                component="span"
                sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary" }}
              >
                {t("Historia")} ({historyCount})
              </Typography>
              {lastHistoryEvent && (
                <Typography
                  component="span"
                  sx={{ fontSize: "0.8125rem", fontWeight: 400, color: "text.secondary" }}
                >
                  — {t("ostatnio")}: {historyEventText(
                    lastHistoryEvent,
                    workflow.tasks
                  )}
                  {lastHistoryEvent.occurredAt
                    ? `, ${formatDateTime(lastHistoryEvent.occurredAt)}`
                    : ""}
                </Typography>
              )}
            </Box>
          )}
        </Button>

        <Collapse in={historyOpen} unmountOnExit>
          <Box
            sx={{
              ml: { xs: 0, sm: 1.5 },
              mt: 0.25,
              mb: 0.25,
              pl: { xs: 0, sm: 1.5 },
              borderLeft: { xs: "none", sm: "2px solid" },
              borderColor: { sm: "divider" },
            }}
          >
            {historyError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {historyError}
              </Alert>
            )}

            {!historyLoading && historyEvents.length === 0 && !historyError && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 1, fontSize: "0.8125rem", lineHeight: 1.45 }}
              >
                {t("Brak wpisów w historii workflow.")}
              </Typography>
            )}

            <Stack spacing={0}>
              {historyEvents.map((event, index) => {
                const presentation = historyEventPresentation(event);

                return (
                  <Stack
                    key={`${event.id}-${event.entryNo ?? index}`}
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    spacing={{ xs: 0.5, sm: 1.5 }}
                    sx={{
                      py: 0.85,
                      borderTop: index > 0 ? "1px solid" : "none",
                      borderColor: "divider",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{ minWidth: 0, flex: 1 }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          mt: 0.1,
                          borderRadius: "50%",
                          border: "1.5px solid",
                          borderColor: presentation.color,
                          color: presentation.color,
                          bgcolor: "background.paper",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "0 0 auto",
                        }}
                      >
                        {presentation.icon}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            lineHeight: 1.45,
                            color: "text.primary",
                          }}
                        >
                          {historyEventText(event, workflow.tasks)}
                        </Typography>
                        {shouldShowHistoryComment(event) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mt: 0.2,
                              fontSize: "0.75rem",
                              lineHeight: 1.45,
                            }}
                          >
                            {event.comment}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        whiteSpace: "nowrap",
                        pl: { xs: 4, sm: 0 },
                        flex: "0 0 auto",
                        fontSize: "0.75rem",
                        lineHeight: 1.4,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatDateTime(event.occurredAt)}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Collapse>
      </Box>
    </>
  );

  if (isTerminal) {
    const terminal = terminalPresentation(effectiveInstanceStatus);
    const returnCount = history?.summary.returned ?? workflow.returnCount ?? 0;
    const terminalNodeLabel = lastHistoryEvent
      ? historyEventNode(lastHistoryEvent, workflow.tasks)
      : historyNodeLabel(workflow.currentNodeNo, workflow.tasks);
    const terminalOccurredAt =
      lastHistoryEvent?.occurredAt ??
      workflow.closedAt ??
      history?.instance?.closedAt ??
      null;

    return (
      <Paper
        sx={{
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, py: 1.35 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={1.25}
          >
            <Stack
              direction="row"
              spacing={1.15}
              alignItems="flex-start"
              sx={{ minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  mt: 0.1,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "common.white",
                  bgcolor: terminal.color,
                  flex: "0 0 auto",
                }}
              >
                {terminal.icon}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={0.8}
                  alignItems="baseline"
                  flexWrap="wrap"
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: terminal.color,
                    }}
                  >
                    {t(terminal.label)}
                  </Typography>

                  {workflow.workflowCode && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      }}
                    >
                      Workflow: {workflow.workflowCode}
                    </Typography>
                  )}
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.8}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ mt: 0.15 }}
                >
                  {(terminalNodeLabel || terminalOccurredAt) && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.6875rem",
                        lineHeight: 1.45,
                        color: "text.secondary",
                      }}
                    >
                      {terminalNodeLabel
                        ? `Na etapie ${terminalNodeLabel}`
                        : t("Proces zakończony")}
                      {terminalOccurredAt
                        ? ` · ${formatRelativeHistoryDateTime(
                            terminalOccurredAt
                          )}`
                        : ""}
                    </Typography>
                  )}

                  {returnCount > 0 && (
                    <Chip
                      size="small"
                      label={returnCountLabel(returnCount)}
                      sx={{
                        height: 20,
                        bgcolor: "warning.50",
                        color: "warning.dark",
                        "& .MuiChip-label": {
                          px: 0.9,
                          fontSize: "0.625rem",
                          fontWeight: 600,
                        },
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            <Button
              size="small"
              color="inherit"
              onClick={() => setHistoryOpen((current) => !current)}
              endIcon={
                historyOpen ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 17 }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ fontSize: 17 }} />
                )
              }
              sx={{
                alignSelf: { xs: "flex-end", sm: "center" },
                minWidth: 0,
                px: 0.65,
                py: 0.4,
                textTransform: "none",
                color: "text.primary",
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box component="span">{t("Historia")}</Box>
                <Box
                  component="span"
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: 999,
                    bgcolor: "action.hover",
                    color: "text.secondary",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                  }}
                >
                  {historyLoading ? "…" : historyCount}
                </Box>
              </Stack>
            </Button>
          </Stack>
        </Box>

        <Collapse in={historyOpen} unmountOnExit>
          <Divider />
          <Box sx={{ px: 2.1, py: 1.6 }}>
            {renderHistoryTimeline()}
          </Box>
        </Collapse>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        mb: 2,
        px: 2.25,
        py: 1.5,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={{ xs: 1.5, lg: 2 }}
        alignItems={{ xs: "stretch", lg: "center" }}
      >
        <Typography
          variant="body1"
          sx={{
            minWidth: { lg: 160 },
            whiteSpace: "nowrap",
            fontSize: "0.9375rem",
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: 0.01,
            color: "text.primary",
          }}
        >
          {workflow.workflowCode || t("Workflow akceptacji")}
        </Typography>

        {workflow.tasks.length > 0 && (
          <Box sx={{ overflowX: "auto", flex: "0 0 auto" }}>
            <Stack
              direction="row"
              alignItems="center"
              sx={{ minWidth: "max-content" }}
            >
              {workflow.tasks.map((task, index) => {
                const isCurrent = task === currentTask;
                const presentation = nodePresentation(task, isCurrent);
                const previousTask = index > 0 ? workflow.tasks[index - 1] : null;
                const connectorDone =
                  previousTask?.status === "Approved" ||
                  previousTask?.status === "Skipped";

                return (
                  <Stack
                    key={`${task.nodeNo}-${task.visitNo ?? 0}-${index}`}
                    direction="row"
                    alignItems="center"
                  >
                    {index > 0 && (
                      <Box
                        sx={{
                          width: 26,
                          height: 3,
                          borderRadius: 999,
                          bgcolor: connectorDone ? "success.main" : "grey.400",
                        }}
                      />
                    )}

                    <Tooltip
                      title={`${task.name || `${t("Węzeł")} ${index + 1}`} · ${task.status}`}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          border: "2px solid",
                          borderStyle: presentation.dashed ? "dashed" : "solid",
                          borderColor: presentation.borderColor,
                          backgroundColor: presentation.backgroundColor,
                          color: presentation.color,
                          boxShadow: isCurrent
                            ? "0 0 0 3px rgba(25, 118, 210, 0.14)"
                            : task.status === "Approved"
                            ? "0 0 0 1px rgba(46, 125, 50, 0.18)"
                            : "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 12,
                          flex: "0 0 auto",
                        }}
                      >
                        {presentation.icon ?? index + 1}
                      </Box>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap"
          sx={{ flex: 1, minWidth: 0 }}
        >
          {currentTask && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem", lineHeight: 1.45, letterSpacing: 0 }}
            >
              {t("Węzeł")} {currentIndex + 1} · {currentTask.name || "—"}
              {workflow.currentUser.isApprover && !workflow.currentUser.approved ? (
                <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                  {` · ${t("u Ciebie")}`}
                </Box>
              ) : workflow.approvers.length > 0 ? (
                <Box component="span"> {` · ${t("u")}:`}</Box>
              ) : null}
            </Typography>
          )}

          {!workflow.currentUser.isApprover && workflow.approvers.length > 0 &&
            workflow.approvers.map((approver) => (
              <Tooltip
                key={approver.email}
                title={`${approverLabel(approver)} · ${
                  approver.approved ? t("zatwierdzono") : t("oczekuje")
                }`}
              >
                <Avatar
                  sx={{
                    width: 25,
                    height: 25,
                    fontSize: 10,
                    fontWeight: 600,
                    border: "1px solid",
                    borderColor: approver.approved ? "success.main" : "divider",
                  }}
                >
                  {approverInitials(approver)}
                </Avatar>
              </Tooltip>
            ))}

          {requiredApprovals > 0 && (
            <Chip
              size="small"
              label={`${currentApprovals} ${t("z")} ${requiredApprovals} ${t("wymaganych zgód")}`}
              sx={{
                height: 26,
                bgcolor: "success.50",
                color: "success.dark",
                "& .MuiChip-label": {
                  px: 1.1,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  letterSpacing: 0,
                },
              }}
            />
          )}

          {currentDueLabel && (
            <Chip
              size="small"
              label={currentDueLabel}
              sx={{
                height: 26,
                bgcolor: "warning.50",
                color: "warning.dark",
                "& .MuiChip-label": {
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  letterSpacing: 0,
                },
              }}
            />
          )}
        </Stack>

        {canTakeAction && (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              color="success"
              disabled={actionBusy}
              onClick={() => void executeDecision("Approved")}
              sx={{
                textTransform: "none",
                whiteSpace: "nowrap",
                fontSize: "0.875rem",
                fontWeight: 600,
                letterSpacing: 0,
              }}
            >
              {decisionLoading === "Approved" ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                t("Zatwierdź")
              )}
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={actionBusy}
              onClick={() => {
                setDecisionError(null);
                setComment("");
                setDialogDecision("Rejected");
              }}
              sx={{
                textTransform: "none",
                whiteSpace: "nowrap",
                fontSize: "0.875rem",
                fontWeight: 600,
                letterSpacing: 0,
              }}
            >
              {t("Odrzuć")}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              disabled={actionBusy}
              onClick={() => {
                setDecisionError(null);
                setComment("");
                setDialogDecision("Returned");
              }}
              sx={{
                textTransform: "none",
                whiteSpace: "nowrap",
                fontSize: "0.875rem",
                fontWeight: 600,
                letterSpacing: 0,
              }}
            >
              {t("Cofnij")}
            </Button>
          </Stack>
        )}
      </Stack>

      {decisionError && (
        <Alert severity="error" onClose={() => setDecisionError(null)} sx={{ mt: 1.25 }}>
          {decisionError}
        </Alert>
      )}

      {renderHistory()}

      <Dialog
        open={commentRequired}
        onClose={() => {
          if (actionBusy) return;
          setDialogDecision(null);
          setComment("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogDecision === "Returned"
            ? t("Cofnij zadanie workflow")
            : t("Odrzuć zadanie workflow")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {dialogDecision === "Returned"
              ? t("Podaj powód cofnięcia zadania. Komentarz jest wymagany.")
              : t("Podaj powód odrzucenia zadania. Komentarz jest wymagany.")}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label={t("Komentarz")}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            inputProps={{ maxLength: 500 }}
            disabled={actionBusy}
            helperText={`${comment.length}/500`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogDecision(null);
              setComment("");
            }}
            disabled={actionBusy}
          >
            {t("Anuluj")}
          </Button>
          <Button
            variant="contained"
            color={dialogDecision === "Rejected" ? "error" : "warning"}
            disabled={!dialogDecision || !trimmedComment || actionBusy}
            onClick={() => {
              if (!dialogDecision || !trimmedComment) return;
              void executeDecision(dialogDecision, trimmedComment);
            }}
          >
            {decisionLoading === dialogDecision ? (
              <CircularProgress size={18} color="inherit" />
            ) : dialogDecision === "Returned" ? (
              t("Cofnij")
            ) : (
              t("Odrzuć")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
